import { Box, Button, Modal, Typography, useTheme, Input } from '@mui/material';
import { strings } from 'i18n/Strings';
import React from 'react';
import { colorTheme } from 'theme';

/**
 * Props interface for the AlertModal component
 */
export interface IDialogProps {
  /** Whether the modal is open or closed */
  open: boolean;
  /** Callback function to close the modal */
  onClose: () => void;
  /** Title text to display in the modal header */
  title?: string;
  /** React nodes to render as modal content */
  children?: React.ReactNode;
  /** Async function to handle delete operations */
  handleDelete?: () => Promise<void>;
  /** Async function to handle re-registration operations */
  handleReRegister?: () => Promise<void>;
  /** Whether the modal is for organization employee actions */
  isOrganizationEmployee?: boolean;
  /** Whether to show alert message */
  isShowAlertMessage?: boolean;
  /** Alert message text to display */
  alertMessage?: string;
  /** Comments text for organization employee actions */
  comments?: string;
  /** Function to update comments state */
  setComments?: (value: string) => void;
  /** Whether to show re-register button */
  isButtonForReregister?: boolean;
  /** Whether to show confirmation button */
  isButtonForConfirmation?: boolean;
  /** Async function to handle confirmation operations */
  handleConfirm?: () => Promise<void>;
  /** Email address to display in the modal */
  email?: string;
}

/**
 * AlertModal component for displaying confirmation dialogs and alerts
 * Supports various actions like delete, re-register, and confirmation
 * with optional comment input for organization employee actions
 *
 * @param props - Component props containing modal configuration and handlers
 * @returns JSX element representing the alert modal
 */
const AlertModal = (props: IDialogProps): JSX.Element => {
  const {
    title,
    onClose,
    open,
    handleDelete,
    handleReRegister,
    isOrganizationEmployee,
    isShowAlertMessage,
    comments,
    setComments,
    isButtonForReregister,
    isButtonForConfirmation,
    handleConfirm,
    email,
  } = props;
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',

          bgcolor: 'background.paper',
          boxShadow: 24,
          py: 5,
          px: 5,
          borderRadius: 5,
        }}
      >
        {/* Modal header with title and optional email */}
        {email ? (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center', marginBottom: 1 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center', marginBottom: 3 }}
            >
              {email} ?
            </Typography>
          </>
        ) : (
          <Typography variant="h5" component="h2" sx={{ textAlign: 'center' }}>
            {title}
          </Typography>
        )}

        {/* Comments input for organization employee actions */}
        {isOrganizationEmployee && !isShowAlertMessage ? (
          <Input
            autoFocus
            value={comments}
            onChange={(
              event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
            ) => {
              if (setComments) setComments(event.target.value);
            }}
            margin="dense"
            placeholder="Comments (if any)"
            type="text"
            fullWidth
            variant="standard"
          />
        ) : null}

        {/* Action buttons container */}
        <Box
          sx={{
            pt: 3,
            display: 'flex',
            justifyContent: isShowAlertMessage ? 'flex-end' : 'space-between',
          }}
        >
          {!isShowAlertMessage && (
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: 'white',
                borderRadius: 2,
                py: 1.5,
                '&:hover': {
                  bgcolor: colors.gray[800],
                },
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                style={{ fontSize: '12px', color: colors.primaryBg[700] }}
              >
                {strings.displayText.cancel}
              </Typography>
            </Button>
          )}
          <Button
            onClick={
              isShowAlertMessage
                ? onClose
                : isButtonForReregister
                ? handleReRegister
                : isButtonForConfirmation
                ? handleConfirm
                : handleDelete
            }
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              bgcolor: colors.greenAccent[500],
              borderRadius: 2,
              py: 1.5,
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              style={{ fontSize: '12px' }}
            >
              {isShowAlertMessage
                ? 'Ok'
                : isButtonForReregister
                ? 'Register'
                : isButtonForConfirmation
                ? 'Yes, Confirm'
                : 'Delete'}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AlertModal;
